import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  Collapse,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  TextField,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Attachment as AttachmentIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Star as StarIcon,
  Flag as FlagIcon,
  DriveFileMove as MoveIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Report as SpamIcon,
  MoveToInbox as InboxIcon,
  MailOutline as MailOutlineIcon,
  CheckCircle as CheckCircleIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewStream as ViewStreamIcon,
  ViewHeadline as ViewHeadlineIcon,
  VerticalSplit as VerticalSplitIcon,
  HorizontalSplit as HorizontalSplitIcon,
  WebAsset as NoSplitIcon,
  Refresh as RefreshIcon,
  Snooze as SnoozeIcon,
  TaskAlt as AddToTasksIcon,
  Label as LabelAsIcon,
  ForwardToInbox as ForwardAttachmentIcon,
  FilterList as FilterMessagesIcon,
  VolumeOff as MuteIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Add as AddLabelIcon,
  Settings as ManageLabelsIcon,
} from '@mui/icons-material';
import { useMailStore } from '../../stores/mailStore';
import { useThemeStore } from '../../stores/themeStore';
import EmailDetailView from './EmailDetailView';
import { format, isToday, isYesterday, isThisMonth, isThisYear } from 'date-fns';

// System MIME types that should not be shown as user attachments
const SYSTEM_MIME_TYPES = ['message/delivery-status', 'message/rfc822', 'text/rfc822-headers', 'application/pgp-signature', 'application/pkcs7-signature', 'application/x-pkcs7-signature', 'message/disposition-notification'];

// Get only real user-attached files (exclude inline, system, auto-generated)
const getRealAttachments = (attachments: any[] | undefined | null) => {
  if (!attachments) return [];
  return attachments.filter((att: any) => {
    if (att.contentDisposition === 'inline') return false;
    if (att.contentId) return false;
    if (SYSTEM_MIME_TYPES.includes(att.contentType?.toLowerCase())) return false;
    if (att.filename === 'attachment' && (!att.contentType || att.contentType.startsWith('message/') || att.contentType.startsWith('text/rfc822'))) return false;
    return true;
  });
};

// Conversation/Thread group interface
interface EmailThread {
  threadId: string;
  emails: any[];
  latestEmail: any;
  unreadCount: number;
  participantCount: number;
}

// Email Card Component with hover actions
interface EmailCardProps {
  email: any;
  isSelected: boolean;
  isActive: boolean;
  isDark: boolean;
  isSentFolder?: boolean;
  isMobile?: boolean;
  onSelect: () => void;
  onClick: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

const EmailCard: React.FC<EmailCardProps> = ({
  email,
  isSelected,
  isActive,
  isDark,
  isSentFolder = false,
  isMobile = false,
  onSelect,
  onClick,
  onArchive,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { densityMode } = useThemeStore();

  // Zoho-like compact density values
  const densityPy = densityMode === 'compact' ? 0.15 : densityMode === 'cozy' ? 0.35 : 0.6;
  const densityMinH = densityMode === 'compact' ? 28 : densityMode === 'cozy' ? 36 : 44;
  const densityFontSize = '0.844rem';
  const densitySubFontSize = densityMode === 'compact' ? '0.72rem' : densityMode === 'cozy' ? '0.76rem' : '0.8rem';

  const formatEmailDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    return format(date, 'EEE MMM d h:mm a').toUpperCase();
  };

  const getSenderDisplay = () => {
    if (isSentFolder) {
      const to = Array.isArray(email.to) ? email.to[0] : email.to;
      const name = typeof to === 'string' ? to : (to?.name || '');
      const addr = typeof to === 'string' ? to : (to?.address || '');
      if (name && name !== addr) return `"${name}"<${addr}>`;
      return addr || 'Unknown';
    }
    return typeof email.from === 'string'
      ? email.from
      : (email.from?.name || email.from?.address || 'Unknown');
  };

  // Extract preview text from email body (plain text or HTML)
  const getPreviewText = (maxLength: number = 100): string => {
    let text = '';
    if (email.textBody) {
      text = email.textBody;
    } else if (email.htmlBody) {
      // Strip HTML tags and get plain text
      const tmp = document.createElement('div');
      tmp.innerHTML = email.htmlBody;
      text = tmp.textContent || tmp.innerText || '';
    }
    // Clean up: remove extra whitespace, newlines
    text = text.replace(/\s+/g, ' ').trim();
    if (text.length > maxLength) {
      text = text.substring(0, maxLength) + '...';
    }
    return text;
  };

  return (
    <Box
      onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
      onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: isMobile ? 1.5 : 1,
        py: isMobile ? 1 : densityPy,
        minHeight: isMobile ? 56 : densityMinH,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
        cursor: 'pointer',
        bgcolor: isActive 
          ? (isDark ? 'rgba(21, 97, 232, 0.18)' : '#dce8f8') 
          : isSelected 
            ? (isDark ? 'rgba(21, 97, 232, 0.12)' : '#e8f0fe') 
            : isHovered
              ? (isDark ? 'rgba(255,255,255,0.07)' : '#f2f6fc')
              : !email.isRead
                ? (isDark ? 'rgba(255,255,255,0.03)' : '#ffffff')
                : (isDark ? 'rgba(255,255,255,0.008)' : '#f8f9fa'),
        borderLeft: isActive ? '3px solid #1a73e8' : !email.isRead ? '3px solid rgba(26,115,232,0.45)' : '3px solid transparent',
        boxShadow: isHovered && !isActive ? (isDark ? '0 1px 3px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.07)') : 'none',
        transition: 'background-color 0.1s ease, box-shadow 0.1s ease',
      }}
    >
      {/* Checkbox - hidden on mobile, shows on hover/selected on desktop */}
      {!isMobile && (
        <Checkbox
          size="small"
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          sx={{
            p: 0.2,
            mr: 0.5,
            opacity: (isHovered || isSelected) ? 1 : 0.18,
            transition: 'opacity 0.12s',
            color: isDark ? '#666' : '#5f6368',
            '&.Mui-checked': { color: '#1a73e8' },
            '& .MuiSvgIcon-root': { fontSize: 16 },
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,115,232,0.08)' },
            borderRadius: '4px',
          }}
        />
      )}

      {/* Unread dot on mobile, mail icon on desktop */}
      {isMobile ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: email.isRead ? 'transparent' : '#1561e8',
            border: email.isRead ? `1.5px solid ${isDark ? '#444' : '#ccc'}` : 'none',
            mr: 1.25,
            flexShrink: 0,
          }}
        />
      ) : (
        <MailOutlineIcon sx={{ fontSize: 15, color: isDark ? '#666' : '#c0c0c0', mr: 0.5 }} />
      )}

      {/* Two-line content area - Zoho layout: Line1=sender, Line2=subject+date */}
      <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Line 1: Sender ONLY (Zoho style) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.1 }}>
          <Typography
            sx={{
              fontWeight: email.isRead ? 500 : 600,
              color: isDark ? (email.isRead ? '#999' : '#e0e0e0') : (email.isRead ? '#2d2d2d' : '#0d0d0d'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: densityFontSize,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '-0.01em',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {getSenderDisplay()}
          </Typography>
        </Box>

        {/* Line 2: [attachment_icon] Subject ... [att_size] [green_check] [date] | [hover icons] */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getRealAttachments(email.attachments).length > 0 && (
            <AttachmentIcon sx={{ fontSize: 13, color: isDark ? '#666' : '#999', mr: 0.5, flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              fontWeight: email.isRead ? 450 : 500,
              color: isDark ? (email.isRead ? '#888' : '#ccc') : (email.isRead ? '#444' : '#1a1a1a'),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: densitySubFontSize,
              letterSpacing: '-0.01em',
              WebkitFontSmoothing: 'antialiased',
              textRendering: 'optimizeLegibility',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {email.subject || '(no subject)'}
          </Typography>

          {/* Right side of line 2 */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, gap: 0.5 }}>
            {!isMobile && isHovered ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Archive" placement="top">
                  <IconButton size="small" onClick={onArchive} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                    <ArchiveIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" placement="top">
                  <IconButton size="small" onClick={onDelete} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                    <DeleteIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                {/* Attachment count badge (Gmail-style) */}
                {(() => {
                  const ra = getRealAttachments(email.attachments);
                  return ra.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        bgcolor: '#d3e3fd',
                        color: '#1967d2',
                        borderRadius: '3px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      {ra.length}
                    </Box>
                  ) : null;
                })()}
                {isSentFolder && (
                  <CheckCircleIcon sx={{ fontSize: 14, color: '#34a853' }} />
                )}
                <Typography
                  sx={{ 
                    color: isDark ? '#777' : '#5f6368', 
                    fontSize: '0.72rem',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatEmailDate(email.receivedAt || email.date || email.createdAt)}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Line 3: Preview text (Gmail-style) */}
        {!isMobile && (
          <Box sx={{ mt: 0.25 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: isDark ? '#666' : '#5f6368',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}
            >
              {getPreviewText(120)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Thread Card Component for conversation view
interface ThreadCardProps {
  thread: EmailThread;
  isSelected: boolean;
  isActive: boolean;
  isExpanded: boolean;
  isDark: boolean;
  isSentFolder?: boolean;
  isMobile?: boolean;
  onToggleExpand: () => void;
  onSelectThread: () => void;
  onSelectEmail: (email: any) => void;
  onStar: (email: any) => void;
  onArchive: (email: any) => void;
  onDelete: (email: any) => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
  thread,
  isSelected,
  isActive,
  isExpanded,
  isDark,
  isSentFolder = false,
  isMobile = false,
  onToggleExpand,
  onSelectThread,
  onSelectEmail,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onStar: _onStar,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onArchive: _onArchive,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDelete: _onDelete,
}) => {
  const email = thread.latestEmail;
  const [isHovered, setIsHovered] = useState(false);
  const { densityMode } = useThemeStore();

  // Zoho-like compact density values
  const densityPy = densityMode === 'compact' ? 0.15 : densityMode === 'cozy' ? 0.35 : 0.6;
  const densityMinH = densityMode === 'compact' ? 28 : densityMode === 'cozy' ? 36 : 44;
  const densityFontSize = '0.844rem';
  const densitySubFontSize = densityMode === 'compact' ? '0.72rem' : densityMode === 'cozy' ? '0.76rem' : '0.8rem';

  const formatEmailDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return format(date, 'h:mm a');
    return format(date, 'EEE MMM d h:mm a').toUpperCase();
  };

  const getSenderDisplay = () => {
    if (isSentFolder) {
      if (thread.emails.length === 1) {
        // Single sent email - show recipient like Zoho: "Name"<email>
        const tos = Array.isArray(email.to) ? email.to : [email.to];
        const firstTo = tos[0];
        const name = typeof firstTo === 'string' ? firstTo : (firstTo?.name || '');
        const addr = typeof firstTo === 'string' ? firstTo : (firstTo?.address || '');
        if (name && name !== addr) return `"${name}"<${addr}>`;
        return addr || 'Unknown';
      }
      // Multi-email thread: Me... other recipients
      const names: string[] = [];
      const seen = new Set<string>();
      thread.emails.forEach((e: any) => {
        const tos = Array.isArray(e.to) ? e.to : [e.to];
        tos.forEach((t: any) => {
          const n = typeof t === 'string' ? t : (t?.name || t?.address || '');
          if (n && !seen.has(n)) { seen.add(n); names.push(n); }
        });
      });
      return `Me... ${names.join(', ')}` || 'Unknown';
    }
    // Inbox: collect unique sender names
    const names: string[] = [];
    const seen = new Set<string>();
    thread.emails.forEach((e: any) => {
      const name = typeof e.from === 'string' ? e.from : (e.from?.name || e.from?.address || '');
      if (name && !seen.has(name)) { seen.add(name); names.push(name); }
    });
    return names.join('  ') || 'Unknown';
  };

  // Extract preview text from email body (plain text or HTML)
  const getPreviewText = (maxLength: number = 100): string => {
    let text = '';
    if (email.textBody) {
      text = email.textBody;
    } else if (email.htmlBody) {
      // Strip HTML tags and get plain text
      const tmp = document.createElement('div');
      tmp.innerHTML = email.htmlBody;
      text = tmp.textContent || tmp.innerText || '';
    }
    // Clean up: remove extra whitespace, newlines
    text = text.replace(/\s+/g, ' ').trim();
    if (text.length > maxLength) {
      text = text.substring(0, maxLength) + '...';
    }
    return text;
  };

  return (
    <Box>
      <Box
        onMouseEnter={isMobile ? undefined : () => setIsHovered(true)}
        onMouseLeave={isMobile ? undefined : () => setIsHovered(false)}
        onClick={() => thread.emails.length > 1 ? onToggleExpand() : onSelectEmail(email)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 1.5 : 1,
          py: isMobile ? 1 : densityPy,
          minHeight: isMobile ? 56 : densityMinH,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
          cursor: 'pointer',
          bgcolor: isActive 
            ? (isDark ? 'rgba(21, 97, 232, 0.18)' : '#dce8f8') 
            : isSelected 
              ? (isDark ? 'rgba(21, 97, 232, 0.12)' : '#e8f0fe') 
              : isHovered
                ? (isDark ? 'rgba(255,255,255,0.07)' : '#f2f6fc')
                : thread.unreadCount > 0 
                  ? (isDark ? 'rgba(255,255,255,0.03)' : '#ffffff') 
                  : (isDark ? 'rgba(255,255,255,0.008)' : '#f8f9fa'),
          borderLeft: isActive ? '3px solid #1a73e8' : thread.unreadCount > 0 ? '3px solid rgba(26,115,232,0.45)' : '3px solid transparent',
          boxShadow: isHovered && !isActive ? (isDark ? '0 1px 3px rgba(0,0,0,0.35)' : '0 1px 4px rgba(0,0,0,0.07)') : 'none',
          transition: 'background-color 0.1s ease, box-shadow 0.1s ease',
        }}
      >
        {/* Checkbox - hidden on mobile, shows on hover/selected on desktop */}
        {!isMobile && (
          <Checkbox
            size="small"
            checked={isSelected}
            onClick={(e) => { e.stopPropagation(); onSelectThread(); }}
            sx={{ 
              p: 0.2, 
              mr: 0.25,
              opacity: (isHovered || isSelected) ? 1 : 0.18,
              transition: 'opacity 0.12s',
              color: isDark ? '#666' : '#5f6368',
              '&.Mui-checked': { color: '#1a73e8' },
              '& .MuiSvgIcon-root': { fontSize: 16 },
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(26,115,232,0.08)' },
              borderRadius: '4px',
            }}
          />
        )}

        {/* Expand arrow for threads */}
        {thread.emails.length > 1 ? (
          <IconButton size="small" sx={{ p: 0.1, mr: 0.25, color: isDark ? '#666' : '#bbb' }}>
            {isExpanded ? <ArrowDownIcon sx={{ fontSize: 15 }} /> : <ArrowRightIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        ) : null}

        {/* Unread dot on mobile, mail icon on desktop */}
        {isMobile ? (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: thread.unreadCount > 0 ? '#1561e8' : 'transparent',
              border: thread.unreadCount > 0 ? 'none' : `1.5px solid ${isDark ? '#444' : '#ccc'}`,
              mr: 1.25,
              flexShrink: 0,
            }}
          />
        ) : (
          <MailOutlineIcon sx={{ fontSize: 15, color: isDark ? '#666' : '#c0c0c0', mr: 0.5 }} />
        )}

        {/* Two-line content area - Zoho layout: Line1=sender, Line2=count+subject+date */}
        <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
          {/* Line 1: Sender ONLY (Zoho style) */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.1 }}>
            <Typography
              sx={{
                fontWeight: thread.unreadCount > 0 ? 600 : 500,
                color: isDark ? (thread.unreadCount > 0 ? '#e0e0e0' : '#999') : (thread.unreadCount > 0 ? '#0d0d0d' : '#2d2d2d'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: densityFontSize,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                letterSpacing: '-0.01em',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {getSenderDisplay()}
            </Typography>
          </Box>

          {/* Line 2: [thread_count] [attachment_icon] subject ... [att_size] [green_check] [date] | [hover icons] */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {thread.emails.length > 1 && (
              <Typography
                component="span"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: isDark ? '#888' : '#666',
                  flexShrink: 0,
                  mr: 0.5,
                }}
              >
                {thread.emails.length}
              </Typography>
            )}
            {getRealAttachments(email.attachments).length > 0 && (
              <AttachmentIcon sx={{ fontSize: 13, color: isDark ? '#666' : '#999', mr: 0.5, flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontWeight: thread.unreadCount > 0 ? 500 : 450,
                color: isDark ? (thread.unreadCount > 0 ? '#ccc' : '#888') : (thread.unreadCount > 0 ? '#1a1a1a' : '#444'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: densitySubFontSize,
                letterSpacing: '-0.01em',
                WebkitFontSmoothing: 'antialiased',
                textRendering: 'optimizeLegibility',
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {email.subject || '(no subject)'}
            </Typography>

            {/* Right side of line 2 */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, gap: 0.5 }}>
              {!isMobile && isHovered ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="Delete" placement="top">
                    <IconButton size="small" onClick={() => _onDelete(email)} sx={{ p: 0.25, '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#eee' } }}>
                      <DeleteIcon sx={{ fontSize: 15, color: isDark ? '#aaa' : '#666' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <>
                  {/* Attachment count badge (Gmail-style) */}
                  {(() => {
                    const ra = getRealAttachments(email.attachments);
                    return ra.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 18,
                          height: 18,
                          px: 0.5,
                          bgcolor: '#d3e3fd',
                          color: '#1967d2',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                        }}
                      >
                        {ra.length}
                      </Box>
                    ) : null;
                  })()}
                  {isSentFolder && (
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#34a853' }} />
                  )}
                  <Typography
                    sx={{ 
                      color: isDark ? '#777' : '#5f6368', 
                      fontSize: '0.72rem',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatEmailDate(email.receivedAt || email.date || email.createdAt)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Line 3: Preview text (Gmail-style) */}
          {!isMobile && (
            <Box sx={{ mt: 0.25 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: isDark ? '#666' : '#5f6368',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 400,
                }}
              >
                {getPreviewText(120)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Expanded thread emails */}
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 3.5, bgcolor: isDark ? 'rgba(0,0,0,0.15)' : '#f8f9fa', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}` }}>
          {thread.emails.map((threadEmail, index) => (
            <Box
              key={threadEmail.id}
              onClick={() => onSelectEmail(threadEmail)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.3,
                cursor: 'pointer',
                borderTop: index > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}` : 'none',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#e8f0fe' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: threadEmail.isRead ? 'transparent' : '#1561e8',
                  border: threadEmail.isRead ? `1px solid ${isDark ? '#555' : '#dadce0'}` : 'none',
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 2, fontSize: '0.8rem', fontWeight: 500, letterSpacing: '-0.01em', WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility', color: isDark ? '#ccc' : '#0d0d0d' }}>
                {typeof threadEmail.from === 'string' ? threadEmail.from : (threadEmail.from?.name || threadEmail.from?.address)}
              </Typography>
              <Typography sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem', color: isDark ? '#888' : '#888' }}>
                {threadEmail.textBody?.substring(0, 80) || ''}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: isDark ? '#777' : '#999', whiteSpace: 'nowrap', ml: 1 }}>
                {format(new Date(threadEmail.receivedAt || threadEmail.date), 'MMM d')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Main Email List View Component
const EmailListView: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { folder = 'inbox' } = useParams<{ folder: string }>();
  const {
    emails,
    selectedEmail,
    selectedEmails,
    isLoading,
    fetchEmails,
    fetchEmailsSilent,
    syncEmails,
    setSelectedEmail,
    toggleEmailSelection,
    selectAllEmails,
    clearSelection,
    markAsRead,
    markAsStarred,
    markAsImportant,
    moveToFolder,
    markAsSpam,
    markAsNotSpam,
    deleteEmails,
    currentFolder,
    total,
    // Scheduled email functions
    scheduledEmails,
    fetchScheduledEmails,
    cancelScheduledEmail,
    folders: storeFolders,
    labels,
    addLabel,
    removeLabel,
  } = useMailStore();
  const { readingPanePosition, setReadingPanePosition, densityMode, setDensityMode } = useThemeStore();

  // Helper function to get date group label
  const getDateGroupLabel = (dateStr: string | undefined) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    if (isThisMonth(date)) return `Earlier in ${format(date, 'MMMM')}`;
    if (isThisYear(date)) return format(date, 'MMMM');
    return format(date, 'MMMM yyyy');
  };

  // Group emails by date for display
  const groupedEmails = useMemo(() => {
    const groups: { label: string; emails: any[] }[] = [];
    let currentGroup = '';
    
    emails.forEach(email => {
      const dateStr = email.receivedAt || email.date || email.createdAt;
      const groupLabel = getDateGroupLabel(dateStr);
      
      if (groupLabel !== currentGroup) {
        currentGroup = groupLabel;
        groups.push({ label: groupLabel, emails: [email] });
      } else if (groups.length > 0) {
        groups[groups.length - 1].emails.push(email);
      }
    });
    
    return groups;
  }, [emails]);

  // For scheduled folder, convert scheduled emails to display format
  const isScheduledFolder = folder === 'scheduled';
  const displayEmails = useMemo(() => {
    if (isScheduledFolder) {
      return scheduledEmails
        .filter(s => s.status === 'pending')
        .map(s => ({
          id: s.id,
          messageId: s.id,
          threadId: s.id,
          subject: s.subject,
          from: { address: s.fromAddress },
          to: s.toAddresses.map(a => ({ address: a })),
          body: s.htmlBody || s.textBody || '',
          htmlBody: s.htmlBody,
          textBody: s.textBody,
          date: s.scheduledAt,
          scheduledAt: s.scheduledAt,
          isScheduled: true,
          status: s.status,
          isRead: false,
          isStarred: false,
          isFlagged: false,
          isImportant: false,
          isDraft: false,
          labels: [] as string[],
          folder: 'scheduled',
          attachments: s.attachments || [],
        }));
    }
    return emails;
  }, [isScheduledFolder, scheduledEmails, emails]);

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(null);
  const [labelMenuAnchor, setLabelMenuAnchor] = useState<null | HTMLElement>(null);
  const [labelSearch, setLabelSearch] = useState('');
  const [conversationView, setConversationView] = useState(true);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  // Track if split was auto-opened on email click (to revert on close)
  const [autoSplitOpened, setAutoSplitOpened] = useState(false);
  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);

  // Unused variable for now but kept for future thread view toggle
  void setConversationView;

  // Group emails by threadId for conversation view
  const emailThreads = useMemo<EmailThread[]>(() => {
    if (!conversationView || isScheduledFolder) return [];
    
    const threadMap = new Map<string, any[]>();
    
    displayEmails.forEach(email => {
      const threadId = email.threadId || email.id;
      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, []);
      }
      threadMap.get(threadId)!.push(email);
    });

    // Convert to array and sort emails within each thread
    const threads: EmailThread[] = [];
    threadMap.forEach((threadEmails, threadId) => {
      threadEmails.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      const participants = new Set<string>();
      threadEmails.forEach(e => {
        const addr = typeof e.from === 'string' ? e.from : e.from?.address;
        if (addr) participants.add(addr);
      });

      threads.push({
        threadId,
        emails: threadEmails,
        latestEmail: threadEmails[0],
        unreadCount: threadEmails.filter(e => !e.isRead).length,
        participantCount: participants.size,
      });
    });

    // Sort threads by latest email date
    threads.sort((a, b) => new Date(b.latestEmail.date).getTime() - new Date(a.latestEmail.date).getTime());
    
    return threads;
  }, [displayEmails, conversationView, isScheduledFolder]);

  const toggleThreadExpand = (threadId: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };

  useEffect(() => {
    // Keep reading pane open when folder changes (hybrid 3-pane layout)
    setSelectedEmail(null);
    // Don't change readingPanePosition - keep it as user preference
    setAutoSplitOpened(false);
    // Handle scheduled folder differently
    if (folder === 'scheduled') {
      fetchScheduledEmails();
    } else {
      // Always fetch emails when folder changes or on initial mount
      fetchEmails(folder, 1);
    }
  }, [folder, fetchEmails, fetchScheduledEmails]);

  // Poll for new emails silently (15s first check, then every 60s)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firstPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (firstPollRef.current) clearTimeout(firstPollRef.current);

    const doSilentPoll = () => {
      if (folder === 'scheduled') {
        fetchScheduledEmails();
      } else {
        fetchEmailsSilent(folder);
      }
    };

    // First check after 10 seconds, then every 60 seconds
    firstPollRef.current = setTimeout(() => {
      console.log('[Polling] First silent poll firing now');
      doSilentPoll();
      pollingRef.current = setInterval(() => {
        console.log('[Polling] Interval poll firing');
        doSilentPoll();
      }, 30000);
    }, 10000);

    return () => {
      if (firstPollRef.current) clearTimeout(firstPollRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [folder, fetchEmailsSilent, fetchScheduledEmails]);

  const handleSelectAll = () => {
    if (selectedEmails.length === displayEmails.length) {
      clearSelection();
    } else {
      selectAllEmails();
    }
  };

  const handleBulkMarkAsRead = (read: boolean) => {
    if (selectedEmails.length > 0) {
      markAsRead(selectedEmails, read);
    }
  };

  const handleBulkDelete = () => {
    if (selectedEmails.length > 0) {
      deleteEmails(selectedEmails);
      clearSelection();
    }
  };

  const handleBulkArchive = () => {
    if (selectedEmails.length > 0) {
      moveToFolder(selectedEmails, 'archive');
      clearSelection();
    }
  };

  const handleBulkMove = (targetFolder: string) => {
    if (selectedEmails.length > 0) {
      moveToFolder(selectedEmails, targetFolder);
      clearSelection();
    }
    setMoveMenuAnchor(null);
  };

  // Auto-switch to right split on email click (like Zoho)
  const handleEmailClick = (email: any) => {
    if (isMobile) {
      // Mobile: always full-page email reading (Zoho-style)
      setReadingPanePosition('off');
      setAutoSplitOpened(false);
      setSelectedEmail(email);
      return;
    }
    if (readingPanePosition === 'off') {
      setReadingPanePosition('right');
      setAutoSplitOpened(true);
    }
    setSelectedEmail(email);
  };

  // Close email detail - if auto-split, return to full list view
  const handleCloseDetail = () => {
    setSelectedEmail(null);
    if (autoSplitOpened) {
      setReadingPanePosition('off');
      setAutoSplitOpened(false);
    }
  };

  const getFolderTitle = () => {
    const titles: Record<string, string> = {
      inbox: 'Inbox',
      drafts: 'Drafts',
      sent: 'Sent',
      scheduled: 'Scheduled',
      spam: 'Spam',
      trash: 'Trash',
      starred: 'Starred',
      important: 'Important',
      snoozed: 'Snoozed',
      templates: 'Templates',
      outbox: 'Outbox',
      home: 'Home',
      'all-unread': 'All Unread',
      unread: 'Unread',
      'all-messages': 'All Messages',
      flagged: 'Flagged',
      archived: 'All Archived',
    };
    if (titles[folder]) return titles[folder];
    const customFolder = storeFolders.find(f => f.id === folder);
    if (customFolder) return customFolder.name;
    return folder.charAt(0).toUpperCase() + folder.slice(1);
  };

  const showReadingPane = readingPanePosition !== 'off';
  const hasSelection = selectedEmails.length > 0;

  // Draggable resize for email list / reading pane split
  const [listPanePercent, setListPanePercent] = useState(40); // default 40% for list
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = readingPanePosition === 'bottom' ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      let pct: number;
      if (readingPanePosition === 'bottom') {
        pct = ((ev.clientY - rect.top) / rect.height) * 100;
      } else {
        pct = ((ev.clientX - rect.left) / rect.width) * 100;
      }
      // Clamp between 20% and 75%
      pct = Math.max(20, Math.min(75, pct));
      setListPanePercent(pct);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [readingPanePosition]);

  // When reading pane is off and user clicks an email, show full-page detail
  const isFullPageView = !showReadingPane && selectedEmail;

  // If reading pane is off, show full-page email detail
  if (isFullPageView) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: isDark ? '#0d1117' : '#ffffff' }}>
        {/* Back bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 1 : 1.5,
          py: isMobile ? 0.75 : 0.5,
          minHeight: isMobile ? 48 : 'auto',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0e0e0'}`,
          bgcolor: isDark ? 'transparent' : '#fff',
          gap: 1,
        }}>
          <IconButton 
            size={isMobile ? 'medium' : 'small'}
            onClick={handleCloseDetail}
            sx={{ p: isMobile ? 0.75 : 0.5, color: isDark ? '#9ca3af' : '#5f6368', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
          >
            <ArrowBackIcon sx={{ fontSize: isMobile ? 24 : 20 }} />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '0.875rem', color: isDark ? '#e5e7eb' : '#333' }}>
            {getFolderTitle()}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={() => { moveToFolder([selectedEmail.id], 'archive'); handleCloseDetail(); }} sx={{ color: isDark ? '#9ca3af' : '#5f6368' }}>
                <ArchiveIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small" onClick={() => { deleteEmails([selectedEmail.id]); handleCloseDetail(); }} sx={{ color: isDark ? '#9ca3af' : '#5f6368' }}>
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        {/* Full page email detail */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <EmailDetailView email={selectedEmail} onClose={handleCloseDetail} />
        </Box>

        {/* Move Menu */}
        <Menu anchorEl={moveMenuAnchor} open={Boolean(moveMenuAnchor)} onClose={() => setMoveMenuAnchor(null)}>
          <MenuItem onClick={() => handleBulkMove('inbox')}>Inbox</MenuItem>
          <MenuItem onClick={() => handleBulkMove('archive')}>Archive</MenuItem>
          <MenuItem onClick={() => handleBulkMove('spam')}>Spam</MenuItem>
          <MenuItem onClick={() => handleBulkMove('trash')}>Trash</MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBulkMove('drafts')}>Drafts</MenuItem>
          <MenuItem onClick={() => handleBulkMove('sent')}>Sent</MenuItem>
        </Menu>
        <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
          <MenuItem onClick={() => { handleBulkMarkAsRead(true); setMoreMenuAnchor(null); }}><MarkReadIcon sx={{ mr: 1, fontSize: 20 }} /> Mark all as read</MenuItem>
          <MenuItem onClick={() => { handleBulkMarkAsRead(false); setMoreMenuAnchor(null); }}><MarkUnreadIcon sx={{ mr: 1, fontSize: 20 }} /> Mark all as unread</MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: isDark ? '#0d1117' : '#ffffff',
      }}
    >
      {/* Email List Panel Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: readingPanePosition === 'bottom' ? 'column' : 'row',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
      {/* Email List Panel */}
      <Box
        sx={{
          width: showReadingPane && readingPanePosition === 'right' ? `${listPanePercent}%` : '100%',
          height: showReadingPane && readingPanePosition === 'bottom' ? `${listPanePercent}%` : '100%',
          minWidth: showReadingPane && readingPanePosition === 'right' ? 280 : 'auto',
          borderRight: showReadingPane && readingPanePosition === 'right' ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}` : 'none',
          borderBottom: showReadingPane && readingPanePosition === 'bottom' ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}` : 'none',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDark ? '#0d1117' : '#ffffff',
        }}
      >
        {/* Selection Toolbar - shows when items are selected */}
        {hasSelection ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed'}`,
              bgcolor: isDark ? 'rgba(26, 115, 232, 0.08)' : '#f2f6fc',
              gap: 0.5,
              minHeight: 48,
            }}
          >
            {/* Checkbox + count */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <Checkbox
                size="small"
                checked={selectedEmails.length === displayEmails.length}
                indeterminate={selectedEmails.length > 0 && selectedEmails.length < displayEmails.length}
                onChange={handleSelectAll}
                sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 }, color: '#1a73e8', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#1a73e8' } }}
              />
              <Typography sx={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#1a73e8', fontSize: '0.8125rem', ml: 0.25, whiteSpace: 'nowrap' }}>
                {selectedEmails.length}
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: '1px', height: 24, bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#d0d0d0', flexShrink: 0 }} />

            {/* Archive */}
            <Tooltip title="Archive">
              <IconButton size="small" onClick={handleBulkArchive} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <ArchiveIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Spam / Not spam */}
            {(folder === 'spam' || currentFolder === 'spam') ? (
              <Tooltip title="Not spam">
                <IconButton size="small" onClick={() => { if (selectedEmails.length > 0) { markAsNotSpam(selectedEmails); clearSelection(); } }} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                  <InboxIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Mark as spam">
                <IconButton size="small" onClick={() => { if (selectedEmails.length > 0) { markAsSpam(selectedEmails); clearSelection(); } }} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                  <SpamIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Delete */}
            <Tooltip title="Delete">
              <IconButton size="small" onClick={handleBulkDelete} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Mark as read / unread */}
            <Tooltip title="Mark as read">
              <IconButton size="small" onClick={() => handleBulkMarkAsRead(true)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MarkReadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Move to */}
            <Tooltip title="Move to">
              <IconButton size="small" onClick={(e) => setMoveMenuAnchor(e.currentTarget)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MoveIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* More (⋮) */}
            <Tooltip title="More">
              <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Box sx={{ flexGrow: 1 }} />

            {/* Clear selection */}
            <Tooltip title="Clear selection">
              <IconButton size="small" onClick={clearSelection} sx={{ color: isDark ? '#9ca3af' : '#444746', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e8eaed' }, borderRadius: '50%' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          /* Normal Header - Zoho style */
          <Box>
            {/* Folder Title Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.5,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e0e0e0'}`,
                bgcolor: isDark ? 'transparent' : '#ffffff',
              }}
            >
              {/* Folder heading with checkbox */}
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.75, 
                  py: 0.5,
                  px: 0,
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedEmails.length === displayEmails.length && displayEmails.length > 0}
                  indeterminate={selectedEmails.length > 0 && selectedEmails.length < displayEmails.length}
                  onChange={handleSelectAll}
                  sx={{ 
                    p: 0.25, 
                    '& .MuiSvgIcon-root': { fontSize: 18 }, 
                    color: isDark ? '#4b5563' : '#bbb',
                    '&.Mui-checked': { color: '#1561e8' },
                    '&.MuiCheckbox-indeterminate': { color: '#1561e8' },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{ 
                    fontWeight: 500, 
                    color: isDark ? '#e5e7eb' : '#202124', 
                    fontSize: '0.8rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {getFolderTitle()}
                </Typography>
{(() => {
                  const folderData = storeFolders.find(f => f.id === folder);
                  const unread = folderData?.unreadCount ?? 0;
                  // Fall back to pagination total for the currently loaded folder
                  const displayTotal = unread > 0 ? unread : (currentFolder === folder ? total : folderData?.totalCount ?? 0);
                  if (!displayTotal) return null;
                  const label = unread > 0
                    ? `${unread.toLocaleString()} Unread ${unread === 1 ? 'email' : 'emails'}`
                    : `${displayTotal.toLocaleString()} ${displayTotal === 1 ? 'email' : 'emails'}`;
                  return (
                    <>
                      <Typography sx={{ color: isDark ? '#4b5563' : '#bdbdbd', fontSize: '0.75rem', mx: 0.5, lineHeight: 1, userSelect: 'none' }}>·</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#6b7280' : '#5f6368', fontWeight: 400 }}>
                        {label}
                      </Typography>
                    </>
                  );
                })()}
              </Box>

              {/* Right side view toggle icons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {/* Refresh */}
                <Tooltip title="Refresh">
                  <IconButton
                    size="small"
                    onClick={() => syncEmails()}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                {/* Density - hidden on mobile */}
                {!isMobile && (
                <Tooltip title={`Density: ${densityMode}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    {densityMode === 'compact' ? <ViewHeadlineIcon sx={{ fontSize: 18 }} /> : densityMode === 'cozy' ? <ViewStreamIcon sx={{ fontSize: 18 }} /> : <ViewAgendaIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                )}

                {/* Reading Pane Position - hidden on mobile */}
                {!isMobile && (
                <Tooltip title={`Reading pane: ${readingPanePosition}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => setViewMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.5, color: isDark ? '#6b7280' : '#999', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0' } }}
                  >
                    {readingPanePosition === 'right' ? <VerticalSplitIcon sx={{ fontSize: 18 }} /> : readingPanePosition === 'bottom' ? <HorizontalSplitIcon sx={{ fontSize: 18 }} /> : <NoSplitIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Loading indicator */}
        {isLoading && <LinearProgress sx={{ height: 2, bgcolor: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(26,115,232,0.1)' }} />}

        {/* Email List */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
            bgcolor: isDark ? 'transparent' : '#ffffff',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              width: '5px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              borderRadius: '3px',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)',
              },
            },
          }}
        >
          {displayEmails.length === 0 && !isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: isDark ? 'rgba(96, 165, 250, 0.08)' : '#f0f4ff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <ArchiveIcon sx={{ fontSize: 36, color: isDark ? '#60a5fa' : '#1561e8', opacity: 0.5 }} />
              </Box>
              <Typography variant="body1" sx={{ color: isDark ? '#e0e0e0' : '#333', fontWeight: 500, mb: 0.5, fontSize: '0.9375rem' }}>
                No emails in {getFolderTitle()}
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#999' : '#999', fontSize: '0.8125rem' }}>
                Messages will appear here
              </Typography>
            </Box>
          ) : conversationView ? (
            /* Conversation/Thread View with Date Groups */
            <>
              {groupedEmails.map((group, groupIndex) => (
                <Box key={group.label}>
                  {/* Date Group Header - Zoho Style */}
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: isDark ? 'rgba(0,0,0,0.15)' : '#f8f9fa',
                      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}`,
                      borderTop: groupIndex > 0 ? `1px solid ${isDark ? 'rgba(255,255,255,0.03)' : '#f0f0f0'}` : 'none',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                    }}
                  >
                    <Typography 
                      sx={{ 
                        color: isDark ? '#999' : '#888', 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        letterSpacing: '0.2px',
                        textTransform: 'none',
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>
                  {/* Emails in this group */}
                  {emailThreads
                    .filter(thread => {
                      const threadDate = thread.latestEmail.receivedAt || thread.latestEmail.date;
                      return getDateGroupLabel(threadDate) === group.label;
                    })
                    .map((thread) => (
                      <ThreadCard
                        key={thread.threadId}
                        thread={thread}
                        isSelected={thread.emails.some(e => selectedEmails.includes(e.id))}
                        isActive={thread.emails.some(e => selectedEmail?.id === e.id)}
                        isExpanded={expandedThreads.has(thread.threadId)}
                        isDark={isDark}
                        isSentFolder={folder === 'sent'}
                        isMobile={isMobile}
                        onToggleExpand={() => toggleThreadExpand(thread.threadId)}
                        onSelectThread={() => thread.emails.forEach(e => toggleEmailSelection(e.id))}
                        onSelectEmail={(email) => handleEmailClick(email)}
                        onStar={(email) => markAsStarred([email.id], !email.isStarred)}
                        onArchive={(email) => moveToFolder([email.id], 'archive')}
                        onDelete={(email) => deleteEmails([email.id])}
                      />
                    ))
                  }
                </Box>
              ))}
            </>
          ) : (
            /* Regular Email List View with Date Groups */
            <>
              {groupedEmails.map((group, groupIndex) => (
                <Box key={group.label}>
                  {/* Date Group Header - Zoho Style */}
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: isDark ? '#0f172a' : '#f8fafc',
                      borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                      borderTop: groupIndex > 0 ? `1px solid ${isDark ? '#334155' : '#e2e8f0'}` : 'none',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                    }}
                  >
                    <Typography 
                      sx={{ 
                        color: isDark ? '#94a3b8' : '#64748b', 
                        fontWeight: 600, 
                        fontSize: '0.75rem', 
                        letterSpacing: '0.3px',
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>
                  {/* Emails in this group */}
                  {group.emails.map((email: any) => (
                    <EmailCard
                      key={email.id}
                      email={email}
                      isSelected={selectedEmails.includes(email.id)}
                      isActive={selectedEmail?.id === email.id}
                      isDark={isDark}
                      isSentFolder={folder === 'sent'}
                      isMobile={isMobile}
                      onSelect={() => toggleEmailSelection(email.id)}
                      onClick={() => {
                        if (email.isScheduled) {
                          handleEmailClick(email);
                        } else {
                          handleEmailClick(email);
                        }
                      }}
                      onArchive={() => {
                        if (email.isScheduled) {
                          cancelScheduledEmail(email.id);
                        } else {
                          moveToFolder([email.id], 'archive');
                        }
                      }}
                      onDelete={() => {
                        if (email.isScheduled) {
                          cancelScheduledEmail(email.id);
                        } else {
                          deleteEmails([email.id]);
                        }
                      }}
                    />
                  ))}
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Draggable Resize Handle */}
      {showReadingPane && (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: readingPanePosition === 'right' ? '4px' : '100%',
            height: readingPanePosition === 'bottom' ? '4px' : '100%',
            cursor: readingPanePosition === 'bottom' ? 'row-resize' : 'col-resize',
            bgcolor: 'transparent',
            flexShrink: 0,
            position: 'relative',
            zIndex: 10,
            '&:hover': {
              bgcolor: isDark ? 'rgba(96,165,250,0.3)' : 'rgba(21,97,232,0.15)',
            },
            '&:active': {
              bgcolor: isDark ? 'rgba(96,165,250,0.5)' : 'rgba(21,97,232,0.3)',
            },
          }}
        />
      )}

      {/* Reading Pane */}
      {showReadingPane && (
        <Box
          sx={{
            flexGrow: 1,
            height: readingPanePosition === 'bottom' ? `${100 - listPanePercent}%` : '100%',
            overflow: 'auto',
            bgcolor: selectedEmail ? theme.palette.background.paper : (isDark ? '#111827' : '#f8f9fa'),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedEmail ? (
            <EmailDetailView email={selectedEmail} onClose={handleCloseDetail} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: isDark ? '#9ca3af' : '#5f6368',
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: isDark ? '#374151' : '#e8eaed',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <MarkReadIcon sx={{ fontSize: 40, color: '#9aa0a6' }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                Select an email to read
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click on an email from the list to view its contents
              </Typography>
            </Box>
          )}
        </Box>
      )}
      </Box>

      {/* Move Menu */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkMove('inbox')}>Inbox</MenuItem>
        <MenuItem onClick={() => handleBulkMove('archive')}>Archive</MenuItem>
        <MenuItem onClick={() => handleBulkMove('spam')}>Spam</MenuItem>
        <MenuItem onClick={() => handleBulkMove('trash')}>Trash</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleBulkMove('drafts')}>Drafts</MenuItem>
        <MenuItem onClick={() => handleBulkMove('sent')}>Sent</MenuItem>
      </Menu>

      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
      >
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <SnoozeIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Snooze
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <AddToTasksIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Add to Tasks
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { markAsStarred(selectedEmails, true); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <StarIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Star selected
        </MenuItem>
        <MenuItem
          onClick={(e) => { setLabelMenuAnchor(e.currentTarget); setLabelSearch(''); }}
          sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LabelAsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            Label as
          </Box>
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleBulkMarkAsRead(true); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MarkReadIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mark as read
        </MenuItem>
        <MenuItem onClick={() => { handleBulkMarkAsRead(false); setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MarkUnreadIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mark as unread
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <ForwardAttachmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Forward as attachment
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <FilterMessagesIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Filter messages like these
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <MuteIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> Mute
        </MenuItem>
      </Menu>

      {/* Label As Submenu */}
      <Menu
        anchorEl={labelMenuAnchor}
        open={Boolean(labelMenuAnchor)}
        onClose={() => setLabelMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 240,
            maxWidth: 280,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            overflow: 'visible',
          },
        }}
      >
        {/* Search box */}
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search labels"
            value={labelSearch}
            onChange={(e) => setLabelSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '0.875rem' },
            }}
          />
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {/* Label list */}
        <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
          {(labels.length === 0
            ? [{ id: 'work', name: 'Work', color: '#ea4335' }, { id: 'personal', name: 'Personal', color: '#34a853' }, { id: 'important', name: 'Important', color: '#fbbc04' }]
            : labels
          )
            .filter((l) => l.name.toLowerCase().includes(labelSearch.toLowerCase()))
            .map((label) => {
              const isApplied = selectedEmails.some((id) => {
                const email = emails.find((e) => e.id === id);
                return email?.labels?.includes(label.id);
              });
              return (
                <MenuItem
                  key={label.id}
                  onClick={() => {
                    if (isApplied) {
                      removeLabel(selectedEmails, label.id);
                    } else {
                      addLabel(selectedEmails, label.id);
                    }
                    setLabelMenuAnchor(null);
                    setMoreMenuAnchor(null);
                  }}
                  sx={{ fontSize: '0.875rem', py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Checkbox
                      size="small"
                      checked={isApplied}
                      disableRipple
                      sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: label.color, flexShrink: 0 }} />
                        {label.name}
                      </Box>
                    }
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
              );
            })}
          {labels.length > 0 && labels.filter((l) => l.name.toLowerCase().includes(labelSearch.toLowerCase())).length === 0 && (
            <Box sx={{ px: 2, py: 1.5, color: 'text.disabled', fontSize: '0.8rem', textAlign: 'center' }}>No labels found</Box>
          )}
        </Box>
        <Divider sx={{ mt: 0.5 }} />
        {/* Footer actions */}
        <MenuItem
          onClick={() => { setLabelMenuAnchor(null); setMoreMenuAnchor(null); /* TODO: open create label dialog */ }}
          sx={{ fontSize: '0.875rem', gap: 1.5, py: 0.75 }}
        >
          <AddLabelIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Create new
        </MenuItem>
        <MenuItem
          onClick={() => { setLabelMenuAnchor(null); setMoreMenuAnchor(null); /* navigate to labels settings */ }}
          sx={{ fontSize: '0.875rem', gap: 1.5, py: 0.75 }}
        >
          <ManageLabelsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Manage labels
        </MenuItem>
      </Menu>

      {/* Density Menu */}
      <Menu
        anchorEl={densityMenuAnchor}
        open={Boolean(densityMenuAnchor)}
        onClose={() => setDensityMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            List Density
          </Typography>
        </Box>
        <MenuItem
          selected={densityMode === 'comfortable'}
          onClick={() => { setDensityMode('comfortable'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewAgendaIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'comfortable' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Comfortable
        </MenuItem>
        <MenuItem
          selected={densityMode === 'cozy'}
          onClick={() => { setDensityMode('cozy'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewStreamIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'cozy' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Cozy
        </MenuItem>
        <MenuItem
          selected={densityMode === 'compact'}
          onClick={() => { setDensityMode('compact'); setDensityMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <ViewHeadlineIcon sx={{ mr: 1.5, fontSize: 18, color: densityMode === 'compact' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Compact
        </MenuItem>
      </Menu>

      {/* Reading Pane / View Menu */}
      <Menu
        anchorEl={viewMenuAnchor}
        open={Boolean(viewMenuAnchor)}
        onClose={() => setViewMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: isDark ? '#e0e0e0' : '#333', mb: 0.5 }}>
            Reading Pane
          </Typography>
        </Box>
        <MenuItem
          selected={readingPanePosition === 'right' || autoSplitOpened}
          onClick={() => { setReadingPanePosition('right'); setAutoSplitOpened(false); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <VerticalSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'right' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Right
        </MenuItem>
        <MenuItem
          selected={readingPanePosition === 'bottom'}
          onClick={() => { setReadingPanePosition('bottom'); setAutoSplitOpened(false); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <HorizontalSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'bottom' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Bottom
        </MenuItem>
        <MenuItem
          selected={readingPanePosition === 'off' && !autoSplitOpened}
          onClick={() => { setReadingPanePosition('off'); setAutoSplitOpened(false); setSelectedEmail(null); setViewMenuAnchor(null); }}
          sx={{ fontSize: '0.84rem' }}
        >
          <NoSplitIcon sx={{ mr: 1.5, fontSize: 18, color: readingPanePosition === 'off' ? '#1a73e8' : (isDark ? '#999' : '#666') }} />
          Off
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EmailListView;
